import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, CalendarDays, BookOpen, Crown, UserPlus, MessageCircle, AlertTriangle, TrendingUp, BarChart3, Video, Target } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#2a7a6f', '#e8945a', '#3b82f6', '#f59e0b'];

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/analytics/overview').then(res => setData(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const funnelData = [
    { name: 'Free', value: data.funnel?.free || 0, color: '#94a3b8' },
    { name: 'Trial', value: data.funnel?.trial || 0, color: '#3b82f6' },
    { name: 'Elite', value: data.funnel?.gold || data.funnel?.elite || 0, color: '#f59e0b' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-1">Growth metrics and user insights</p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <Users className="h-5 w-5 text-primary mb-2" />
          <p className="text-2xl font-semibold">{data.users?.total || 0}</p>
          <p className="text-xs text-muted-foreground">Total Learners</p>
          <p className="text-[10px] text-emerald-600 mt-1">+{data.users?.new_this_month || 0} this month</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-amber-200"><CardContent className="pt-5 pb-4">
          <Crown className="h-5 w-5 text-amber-500 mb-2" />
          <p className="text-2xl font-semibold text-amber-900">{data.users?.gold || 0}</p>
          <p className="text-xs text-amber-700">Elite Members</p>
          <p className="text-[10px] text-muted-foreground mt-1">{data.funnel?.conversion_trial_to_gold || 0}% conversion</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <CalendarDays className="h-5 w-5 text-blue-600 mb-2" />
          <p className="text-2xl font-semibold">{data.sessions?.completed || 0}</p>
          <p className="text-xs text-muted-foreground">Sessions Completed</p>
          <p className="text-[10px] text-muted-foreground mt-1">{data.sessions?.this_month || 0} this month</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <Target className="h-5 w-5 text-emerald-600 mb-2" />
          <p className="text-2xl font-semibold">{data.sessions?.avg_attendance_pct || 0}%</p>
          <p className="text-xs text-muted-foreground">Avg Attendance</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <MessageCircle className="h-5 w-5 text-teal-600 mb-2" />
          <p className="text-2xl font-semibold">{data.soulai?.active_users || 0}</p>
          <p className="text-xs text-muted-foreground">SoulAI Users</p>
          <p className="text-[10px] text-muted-foreground mt-1">{data.soulai?.total_messages || 0} messages</p>
        </CardContent></Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Signups */}
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5" />User Growth</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthly_signups || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(174, 44%, 28%)" radius={[4, 4, 0, 0]} name="New Learners" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Sessions */}
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><BarChart3 className="h-5 w-5" />Sessions Delivered</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.monthly_sessions || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#e8945a" strokeWidth={2} dot={{ r: 4 }} name="Sessions" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversion Funnel */}
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg">Conversion Funnel</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={funnelData} innerRadius={50} outerRadius={80} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                  {funnelData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2">
              {funnelData.map((f, i) => (
                <div key={i} className="flex items-center gap-1 text-xs">
                  <div className="h-2 w-2 rounded-full" style={{ background: f.color }} />{f.name}: {f.value}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Session Breakdown */}
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg">Session Status</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Upcoming</span>
              <span className="text-sm font-semibold">{data.sessions?.upcoming || 0}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(data.sessions?.upcoming || 0) / Math.max(1, data.sessions?.total || 1) * 100}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Completed</span>
              <span className="text-sm font-semibold">{data.sessions?.completed || 0}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${(data.sessions?.completed || 0) / Math.max(1, data.sessions?.total || 1) * 100}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Canceled</span>
              <span className="text-sm font-semibold">{data.sessions?.canceled || 0}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${(data.sessions?.canceled || 0) / Math.max(1, data.sessions?.total || 1) * 100}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg">Platform Overview</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40">
              <span className="text-sm">Total Instructors</span>
              <span className="text-sm font-semibold">{data.instructors || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40">
              <span className="text-sm">Active Batches</span>
              <span className="text-sm font-semibold">{data.batches?.total || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg">
              <span className="text-sm text-muted-foreground">1:1 Batches</span>
              <span className="text-sm">{data.batches?.one_on_one || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg">
              <span className="text-sm text-muted-foreground">Group Batches</span>
              <span className="text-sm">{data.batches?.group || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40">
              <span className="text-sm">Recorded Courses</span>
              <span className="text-sm font-semibold">{data.courses || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40">
              <span className="text-sm">SoulAI Emergencies</span>
              <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200">{data.soulai?.emergencies || 0}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Batches */}
      {data.top_batches?.length > 0 && (
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-lg">Top Batches by Learner Count</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.top_batches.map((b, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl border">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-muted-foreground w-6">#{i + 1}</span>
                    <div>
                      <p className="text-sm font-medium">{b.name}</p>
                      <Badge variant="outline" className="text-[10px]">{b.batch_type === 'one_on_one' ? '1:1' : 'Group'}</Badge>
                    </div>
                  </div>
                  <Badge variant="secondary">{b.learner_count} learners</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
